import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

const PantryItemForm = ({ item, defaultLocation, onSave, onCancel, existingCategories = [] }) => {
  const [formData, setFormData] = useState({
    name: item?.name || '',
    quantity: item ? (item.quantity ?? '') : 1,
    unit: item?.unit || '',
    category: item?.category || '',
    location: item?.location || defaultLocation || 'pantry',
    price: item ? (item.price ?? '') : '',
    expirationDate: item?.expirationDate 
      ? (item.expirationDate instanceof Date 
          ? item.expirationDate.toISOString().split('T')[0]
          : item.expirationDate)
      : '',
  });

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    if (type === 'number') {
      // Allow empty string for easier editing, convert to number on blur/submit
      setFormData(prev => ({
        ...prev,
        [name]: value === '' ? '' : (parseFloat(value) || ''),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      quantity: formData.quantity === '' ? 1 : (parseFloat(formData.quantity) || 0),
      price: formData.price === '' ? 0 : (parseFloat(formData.price) || 0),
      expirationDate: formData.expirationDate || null,
    };
    onSave(submitData);
  };

  const handleBlur = (e) => {
    const { name, type } = e.target;
    if (type === 'number') {
      // Convert empty string to 0 on blur if required field, or keep empty for optional
      const currentValue = formData[name];
      if (currentValue === '') {
        if (name === 'quantity') {
          setFormData(prev => ({ ...prev, [name]: 1 }));
        } else {
          setFormData(prev => ({ ...prev, [name]: 0 }));
        }
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-white rounded-lg shadow">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Item Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">
            Quantity *
          </label>
          <input
            type="number"
            id="quantity"
            name="quantity"
            value={formData.quantity}
            onChange={handleChange}
            onBlur={handleBlur}
            min="0"
            step="0.01"
            required
            placeholder="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>

        <div>
          <label htmlFor="unit" className="block text-sm font-medium text-gray-700 mb-1">
            Unit
          </label>
          <input
            type="text"
            id="unit"
            name="unit"
            value={formData.unit}
            onChange={handleChange}
            placeholder="e.g., lbs, oz, count"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>

      <div>
        <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
          Category
        </label>
        <div className="relative">
          <input
            type="text"
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            placeholder="Type to search or enter new category"
            list="category-options"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            autoComplete="off"
          />
          {existingCategories.length > 0 && (
            <datalist id="category-options">
              {existingCategories.map((category) => (
                <option key={category} value={category} />
              ))}
            </datalist>
          )}
        </div>
        {existingCategories.length > 0 && (
          <p className="mt-1 text-xs text-gray-500">
            Select from existing categories or type to create a new one
          </p>
        )}
      </div>

      <div>
        <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
          Location *
        </label>
        <select
          id="location"
          name="location"
          value={formData.location}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option value="pantry">Pantry</option>
          <option value="refrigerator">Refrigerator</option>
        </select>
      </div>

      <div>
        <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
          Price (per unit)
        </label>
          <input
            type="number"
            id="price"
            name="price"
            value={formData.price}
            onChange={handleChange}
            onBlur={handleBlur}
            min="0"
            step="0.01"
            placeholder="0.00"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
      </div>

      <div>
        <label htmlFor="expirationDate" className="block text-sm font-medium text-gray-700 mb-1">
          Expiration Date
        </label>
        <input
          type="date"
          id="expirationDate"
          name="expirationDate"
          value={formData.expirationDate}
          onChange={handleChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div className="flex space-x-3 pt-2">
        <button
          type="submit"
          className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          {item ? 'Update' : 'Add'} Item
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

PantryItemForm.propTypes = {
  item: PropTypes.object,
  defaultLocation: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  existingCategories: PropTypes.arrayOf(PropTypes.string),
};

export default PantryItemForm;

